import { useQuery } from "@apollo/client";
import { Navigate, useLocation, useParams } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import useCapabilities, { useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import { useMembershipServicesNavLinks } from "../../_membershipServicesNavLinks";
import { NATIONAL_SECTION_NAME, resolveNationalSectionId, sectionOperationsCommitteesPath } from "./constants";
import { NATIONAL_SECTION_QUERY } from "./queries";

function useNationalSectionId(canViewCommittees: boolean) {
  const { data, loading, error } = useQuery(NATIONAL_SECTION_QUERY, {
    variables: { input: { search: NATIONAL_SECTION_NAME } },
    skip: !canViewCommittees
  });
  const nationalSectionId = resolveNationalSectionId(data?.crmSections?.nodes);
  return { nationalSectionId, loading, error };
}

function buildCommitteesSearch(search: string, status: "active" | "all"): string {
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const key of ["highlightRow", "committeeSearch", "core"]) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  if (status === "all") {
    next.set("status", "all");
  }
  const result = next.toString();
  return result ? `?${result}` : "";
}

function buildDetailSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("from");
  const result = params.toString();
  return result ? `?${result}` : "";
}

function RedirectShell({
  title,
  pageTitlePath,
  children
}: {
  title: string;
  pageTitlePath: string;
  children: React.ReactNode;
}) {
  const navLinks = useMembershipServicesNavLinks();
  usePageTitle(pageTitlePath, title);

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      {children}
    </AppLayout>
  );
}

export function NationalCommitteesCatalogRedirect({ status }: { status: "active" | "all" }) {
  const { loaded } = useCapabilities();
  const canViewCommittees = useCanViewCommitteesBool();
  const { nationalSectionId, loading, error } = useNationalSectionId(canViewCommittees);
  const location = useLocation();

  if (!canViewCommittees) {
    if (!loaded) return null;
    return <Navigate to="/people/membership-services" replace />;
  }

  if (loading) return null;

  if (error || !nationalSectionId) {
    return (
      <RedirectShell title="People | Committee Management" pageTitlePath="/people/membership-services/committees">
        {error ? (
          <EmptyState title="Unable to Load National Section" caption={error.message} iconName="error" />
        ) : (
          <EmptyState
            title="National Section Not Found"
            caption="The National Section record is required to load national committees."
            iconName="error"
          />
        )}
      </RedirectShell>
    );
  }

  return (
    <Navigate
      to={`${sectionOperationsCommitteesPath(nationalSectionId)}${buildCommitteesSearch(location.search, status)}${location.hash}`}
      replace
    />
  );
}

export function NationalCommitteeDetailRedirect() {
  const { committeeId } = useParams();
  const { loaded } = useCapabilities();
  const canViewCommittees = useCanViewCommitteesBool();
  const { nationalSectionId, loading, error } = useNationalSectionId(canViewCommittees);
  const location = useLocation();

  if (!canViewCommittees) {
    if (!loaded) return null;
    return <Navigate to="/people/membership-services" replace />;
  }

  if (!committeeId) return null;
  if (loading) return null;

  if (error || !nationalSectionId) {
    return (
      <RedirectShell title="People | Committee" pageTitlePath={`/people/membership-services/committees/${committeeId}`}>
        {error ? (
          <EmptyState title="Unable to Load National Section" caption={error.message} iconName="error" />
        ) : (
          <EmptyState
            title="National Section Not Found"
            caption="The National Section record is required to load national committees."
            iconName="error"
          />
        )}
      </RedirectShell>
    );
  }

  return (
    <Navigate
      to={`${sectionOperationsCommitteesPath(nationalSectionId)}/${committeeId}${buildDetailSearch(location.search)}${location.hash}`}
      replace
    />
  );
}
